/**
 * noiseField — grid-screening-v2 outdoor sound-propagation engine.
 *
 * Screening-grade single-band model over broadband dBA inputs (the only data
 * OEMs supply today): spherical divergence + atmospheric absorption +
 * ISO 9613-2 simplified ground effect + Maekawa barrier diffraction at a
 * representative 500 Hz band. Explicitly NOT an ISO 9613-2 octave-band
 * compliance model — every departure is a named constant and a caveat.
 *
 * Solver-integrity contract:
 *  - Pure + deterministic: identical inputs give byte-identical output
 *    (fixed iteration orders, no wall clock, no Math.hypot, 7-dp ring
 *    rounding). The server re-solves from scenario input alone and replays
 *    at publish requiring hash equality — everything here derives from the
 *    frozen input (noiseSources + noiseGeometry + layout.acousticBarriers +
 *    receptors).
 *  - v1 parity: with atmospheric/ground/barrier terms disabled, the point
 *    kernel reproduces `componentAtDistance` in solveGenerationImpact.ts
 *    exactly (same formulas, same operation order, both datum kinds) — the
 *    map field, the receptor table, and the legacy radial contours agree by
 *    construction. Locked by noiseField.test.ts.
 */

import {
  NOISE_FIELD_MODEL_VERSION,
  type GenerationAcousticBarrier,
  type GenerationNoiseGeometry,
  type GenerationNoiseUnit,
  type NoiseDatum,
  type NoiseFieldResult,
  type NoiseReceptor,
} from '../../types/generationImpact';
import {
  localAxesForPoint,
  latLngForLocalAxes,
  type GenerationParcelTestFitResult,
} from './generationLayoutGeometry';
import { marchingSquaresRings, type ScalarGrid } from '../geometry/marchingSquares';

/* ── Named model constants (screening assumptions, all documented) ────── */

export const SPEED_OF_SOUND_MPS = 343;
/** Single representative band for broadband-dBA screening (λ ≈ 0.686 m). */
export const REPRESENTATIVE_FREQUENCY_HZ = 500;
/** ISO 9613-1 ballpark at 500 Hz, 10 °C / 70 % RH. Screening assumption. */
export const ATM_ABSORPTION_DBA_PER_KM = 2.8;
/** Default acoustic-centre height above grade (casing/stack mid), metres. */
export const DEFAULT_SOURCE_HEIGHT_M = 1.5;
/** Default receiver ear height above grade, metres. */
export const DEFAULT_RECEIVER_HEIGHT_M = 1.5;
/** Single-diffraction practical cap for a thin screen (Maekawa). */
export const BARRIER_MAX_ATTENUATION_DB = 20;
export const NOISE_FIELD_NODE_BUDGET = 4096; // 64 x 64
export const NOISE_FIELD_MAX_EXTENT_M = 5_000;
export const NOISE_FIELD_LEVELS_DBA = [45, 50, 55, 60, 65, 70, 75] as const;
/** ~1 cm; bounds stored Firestore bytes and keeps output deterministic. */
export const NOISE_RING_COORD_DECIMALS = 7;
/** Minimum AOI padding so tiny sources still get a visible field, metres. */
const MIN_AOI_PAD_M = 200;
const MAX_BARRIERS = 64;
const MAX_BARRIER_VERTICES = 256;
export const BARRIER_MIN_HEIGHT_M = 0.5;
export const BARRIER_MAX_HEIGHT_M = 50;

const WAVELENGTH_M = SPEED_OF_SOUND_MPS / REPRESENTATIVE_FREQUENCY_HZ;

export const NOISE_FIELD_CAVEATS = [
  'Screening-grade single-band (500 Hz) propagation model; this is not an ISO 9613-2 octave-band compliance calculation.',
  `Barrier attenuation uses Maekawa thin-screen diffraction capped at ${BARRIER_MAX_ATTENUATION_DB} dB; reflections, multiple diffraction and transmission through screens are excluded.`,
  'Terrain elevation, meteorological refraction and tonal penalties are excluded.',
  'Reference-SPL inputs are not extrapolated inside their measurement/reference distance.',
] as const;

/* ── Public option/typing surface ─────────────────────────────────────── */

export interface NoiseFieldOptions {
  nodeBudget?: number;
  maxExtentM?: number;
  /** ISO simplified ground effect term. Default true. */
  groundAttenuation?: boolean;
  /** Atmospheric absorption term. Default true. */
  atmosphericAbsorption?: boolean;
  receiverHeightM?: number;
}

type ResolvedOptions = Required<NoiseFieldOptions>;

const DEFAULT_OPTIONS: ResolvedOptions = {
  nodeBudget: NOISE_FIELD_NODE_BUDGET,
  maxExtentM: NOISE_FIELD_MAX_EXTENT_M,
  groundAttenuation: true,
  atmosphericAbsorption: true,
  receiverHeightM: DEFAULT_RECEIVER_HEIGHT_M,
};

/** One positioned emitter in the local metre plane. */
export interface LocalNoiseSource {
  noiseSourceId: string;
  label: string;
  datum: NoiseDatum;
  /** Flat source-side allowance carried over from the v1 kernel. */
  sourceBarrierLossDb: number;
  xM: number;
  yM: number;
  heightM: number;
  /** Linear-energy multiplier for this emitter (count spread across positions). */
  energyShare: number;
}

/** One screening segment in the local metre plane. */
export interface LocalBarrierSegment {
  x1: number; y1: number; x2: number; y2: number;
  heightM: number;
}

/* ── Elementary terms ─────────────────────────────────────────────────── */

/** reference-spl → equivalent single-emitter sound power (dBA re 1 pW). */
export function equivalentSoundPowerDbA(datum: NoiseDatum): number {
  if (datum.kind === 'sound-power') {
    return datum.levelDbA + (datum.directivityCorrectionDb ?? 0);
  }
  return datum.levelDbA
    + 10 * Math.log10(4 * Math.PI * datum.referenceDistanceM * datum.referenceDistanceM);
}

export function atmosphericAbsorptionDb(distanceM: number): number {
  return ATM_ABSORPTION_DBA_PER_KM * distanceM / 1000;
}

/** ISO 9613-2 simplified alternative ground term; clamps at 0. */
export function groundAttenuationDb(distanceM: number, meanHeightM: number): number {
  if (distanceM <= 0) return 0;
  const value = 4.8 - (2 * meanHeightM / distanceM) * (17 + 300 / distanceM);
  return value > 0 ? value : 0;
}

/** Maekawa thin-screen loss from over-the-top path difference δ (metres). */
export function maekawaInsertionLossDb(pathDifferenceM: number): number {
  if (!Number.isFinite(pathDifferenceM) || pathDifferenceM < 0) return 0;
  const fresnelN = 2 * pathDifferenceM / WAVELENGTH_M;
  const loss = 10 * Math.log10(3 + 20 * fresnelN);
  if (loss < 0) return 0;
  return loss > BARRIER_MAX_ATTENUATION_DB ? BARRIER_MAX_ATTENUATION_DB : loss;
}

/* ── Barrier screening along one propagation path ─────────────────────── */

/**
 * Proper 2D segment intersection parameter along the source→receiver path,
 * or null when the segments do not cross (parallel counts as no crossing —
 * a path grazing along a wall is not screened by it).
 */
function crossingParameter(
  sx: number, sy: number, rx: number, ry: number,
  seg: LocalBarrierSegment,
): number | null {
  const dx = rx - sx;
  const dy = ry - sy;
  const ex = seg.x2 - seg.x1;
  const ey = seg.y2 - seg.y1;
  const denom = dx * ey - dy * ex;
  if (denom === 0) return null;
  const s = ((seg.x1 - sx) * ey - (seg.y1 - sy) * ex) / denom;
  const t = ((seg.x1 - sx) * dy - (seg.y1 - sy) * dx) / denom;
  if (s < 0 || s > 1 || t < 0 || t > 1) return null;
  return s;
}

/**
 * Dominant-screen barrier attenuation for one source→receiver path.
 * Standard screening practice: the MAX single-barrier Maekawa loss governs
 * (losses are not summed across screens).
 */
export function barrierAttenuationDb(
  sx: number, sy: number, sourceHeightM: number,
  rx: number, ry: number, receiverHeightM: number,
  segments: readonly LocalBarrierSegment[],
): number {
  const dx = rx - sx;
  const dy = ry - sy;
  const horizontalM = Math.sqrt(dx * dx + dy * dy);
  if (horizontalM === 0) return 0;
  const dh = receiverHeightM - sourceHeightM;
  const directM = Math.sqrt(horizontalM * horizontalM + dh * dh);

  let maxLossDb = 0;
  for (const seg of segments) {
    const s = crossingParameter(sx, sy, rx, ry, seg);
    if (s === null) continue;
    const d1 = s * horizontalM;
    const d2 = horizontalM - d1;
    // Line-of-sight height at the crossing; a screen strictly below it does
    // not diffract the path (grazing top ⇒ δ = 0 ⇒ the 4.77 dB Maekawa floor).
    const sightHeightM = sourceHeightM + dh * s;
    if (seg.heightM < sightHeightM) continue;
    const hbS = seg.heightM - sourceHeightM;
    const hbR = seg.heightM - receiverHeightM;
    const overTopM = Math.sqrt(d1 * d1 + hbS * hbS) + Math.sqrt(d2 * d2 + hbR * hbR);
    const loss = maekawaInsertionLossDb(overTopM - directM);
    if (loss > maxLossDb) maxLossDb = loss;
  }
  return maxLossDb;
}

/* ── Point kernel ─────────────────────────────────────────────────────── */

/**
 * A-weighted level at one local point from all emitters, energy-summed.
 * Returns null when any emitter's reference distance encloses the point
 * (never extrapolate inward — mirrors the v1 receptor semantics), or when
 * no emitter contributes energy.
 */
export function noiseLevelAtPointDbA(
  point: { xM: number; yM: number },
  sources: readonly LocalNoiseSource[],
  barrierSegments: readonly LocalBarrierSegment[],
  options: ResolvedOptions,
): number | null {
  let totalEnergy = 0;
  let anyContribution = false;

  for (const source of sources) {
    if (source.energyShare <= 0) continue;
    const dx = point.xM - source.xM;
    const dy = point.yM - source.yM;
    const dh = options.receiverHeightM - source.heightM;
    const slantM = Math.sqrt(dx * dx + dy * dy + dh * dh);

    let baseLevel: number;
    if (source.datum.kind === 'reference-spl') {
      if (slantM < source.datum.referenceDistanceM) return null;
      baseLevel = source.datum.levelDbA
        - 20 * Math.log10(slantM / source.datum.referenceDistanceM)
        - source.sourceBarrierLossDb;
    } else {
      if (slantM <= 0) return null;
      baseLevel = source.datum.levelDbA
        + (source.datum.directivityCorrectionDb ?? 0)
        - 10 * Math.log10(4 * Math.PI * slantM * slantM)
        - source.sourceBarrierLossDb;
    }

    let extraLossDb = 0;
    if (options.atmosphericAbsorption) extraLossDb += atmosphericAbsorptionDb(slantM);
    if (options.groundAttenuation) {
      const horizontalM = Math.sqrt(dx * dx + dy * dy);
      const meanHeightM = (source.heightM + options.receiverHeightM) / 2;
      extraLossDb += groundAttenuationDb(horizontalM, meanHeightM);
    }
    extraLossDb += barrierAttenuationDb(
      source.xM, source.yM, source.heightM,
      point.xM, point.yM, options.receiverHeightM,
      barrierSegments,
    );

    totalEnergy += source.energyShare * 10 ** ((baseLevel - extraLossDb) / 10);
    anyContribution = true;
  }

  if (!anyContribution || totalEnergy <= 0) return null;
  return 10 * Math.log10(totalEnergy);
}

/* ── Input resolution + validation ────────────────────────────────────── */

function assertFiniteLatLng(point: { lat: number; lng: number }, label: string): void {
  if (!Number.isFinite(point.lat) || !Number.isFinite(point.lng)
    || Math.abs(point.lat) > 85 || Math.abs(point.lng) > 180) {
    throw new RangeError(`${label} must be a finite lat/lng inside map bounds`);
  }
}

export function validateAcousticBarriers(
  barriers: readonly GenerationAcousticBarrier[],
): void {
  if (barriers.length > MAX_BARRIERS) {
    throw new RangeError(`at most ${MAX_BARRIERS} acoustic barriers are supported; received ${barriers.length}`);
  }
  for (const barrier of barriers) {
    const minVertices = barrier.kind === 'building' ? 3 : 2;
    if (barrier.path.length < minVertices || barrier.path.length > MAX_BARRIER_VERTICES) {
      throw new RangeError(
        `barrier "${barrier.label}" needs ${minVertices}-${MAX_BARRIER_VERTICES} vertices; received ${barrier.path.length}`,
      );
    }
    if (!Number.isFinite(barrier.heightM)
      || barrier.heightM < BARRIER_MIN_HEIGHT_M || barrier.heightM > BARRIER_MAX_HEIGHT_M) {
      throw new RangeError(
        `barrier "${barrier.label}" height must be ${BARRIER_MIN_HEIGHT_M}-${BARRIER_MAX_HEIGHT_M} m; received ${barrier.heightM}`,
      );
    }
    for (const vertex of barrier.path) assertFiniteLatLng(vertex, `barrier "${barrier.label}" vertex`);
  }
}

function datumForUnit(unit: GenerationNoiseUnit): NoiseDatum | null {
  if (unit.noise) return unit.noise;
  if (unit.soundPowerDbA != null) {
    return {
      kind: 'sound-power',
      levelDbA: unit.soundPowerDbA,
      ...(unit.directivityCorrectionDb !== undefined
        ? { directivityCorrectionDb: unit.directivityCorrectionDb }
        : {}),
    };
  }
  return null;
}

interface ResolvedSources {
  local: LocalNoiseSource[];
  record: NoiseFieldResult['sources'];
}

/** Deterministic id ordering so permuted input arrays yield identical output. */
function byId<T extends { id: string }>(a: T, b: T): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function resolveSources(
  noiseSources: readonly GenerationNoiseUnit[],
  geometry: GenerationNoiseGeometry,
  origin: { lat: number; lng: number },
): ResolvedSources {
  const geometryById = new Map(geometry.sources.map((entry) => [entry.noiseSourceId, entry]));
  const local: LocalNoiseSource[] = [];
  const record: NoiseFieldResult['sources'] = [];

  for (const unit of [...noiseSources].sort(byId)) {
    const datum = datumForUnit(unit);
    if (unit.count <= 0 || datum == null) continue;
    const entry = geometryById.get(unit.id);
    if (!entry) {
      throw new RangeError(`noiseGeometry is missing an entry for noise source "${unit.name}" (${unit.id})`);
    }
    if (entry.positions.length === 0) {
      throw new RangeError(`noiseGeometry entry for "${unit.name}" has no positions`);
    }
    for (const position of entry.positions) assertFiniteLatLng(position, `source "${unit.name}" position`);
    const heightM = entry.heightM ?? DEFAULT_SOURCE_HEIGHT_M;
    if (!Number.isFinite(heightM) || heightM < 0 || heightM > 200) {
      throw new RangeError(`source "${unit.name}" height must be 0-200 m; received ${heightM}`);
    }

    // Spread total acoustic energy evenly across the emitter positions:
    // aggregate reference-SPL never multiplies by count (v1 rule); every
    // other datum carries count × energy in total.
    const positionCount = entry.positions.length;
    const isAggregate = datum.kind === 'reference-spl' && datum.scope === 'aggregate';
    const energyShare = (isAggregate ? 1 : unit.count) / positionCount;
    const sourceBarrierLossDb = unit.barrierLossDb ?? 0;

    for (const position of entry.positions) {
      const localPoint = localAxesForPoint(position, origin, 0);
      local.push({
        noiseSourceId: unit.id,
        label: unit.name,
        datum,
        sourceBarrierLossDb,
        xM: localPoint.xM,
        yM: localPoint.yM,
        heightM,
        energyShare,
      });
    }
    record.push({
      noiseSourceId: unit.id,
      label: unit.name,
      equivalentLwDbA: round2(equivalentSoundPowerDbA(datum)),
      positions: entry.positions.map((p) => ({ lat: p.lat, lng: p.lng })),
      heightM,
    });
  }

  return { local, record };
}

function localBarrierSegments(
  barriers: readonly GenerationAcousticBarrier[],
  origin: { lat: number; lng: number },
): LocalBarrierSegment[] {
  const segments: LocalBarrierSegment[] = [];
  for (const barrier of barriers) {
    const localPath = barrier.path.map((vertex) => localAxesForPoint(vertex, origin, 0));
    const closed = barrier.kind === 'building'
      ? [...localPath, localPath[0]]
      : localPath;
    for (let i = 0; i < closed.length - 1; i++) {
      segments.push({
        x1: closed[i].xM, y1: closed[i].yM,
        x2: closed[i + 1].xM, y2: closed[i + 1].yM,
        heightM: barrier.heightM,
      });
    }
  }
  return segments;
}

/* ── AOI sizing ───────────────────────────────────────────────────────── */

/** Combined free-field 45 dBA radius (v1 equivalent-at-1-m math), for AOI. */
function freeField45RadiusM(sources: readonly LocalNoiseSource[]): number {
  let energy = 0;
  for (const source of sources) {
    if (source.energyShare <= 0) continue;
    const datum = source.datum;
    const oneMetre = datum.kind === 'reference-spl'
      ? datum.levelDbA + 20 * Math.log10(datum.referenceDistanceM) - source.sourceBarrierLossDb
      : datum.levelDbA + (datum.directivityCorrectionDb ?? 0)
        - 10 * Math.log10(4 * Math.PI) - source.sourceBarrierLossDb;
    energy += source.energyShare * 10 ** (oneMetre / 10);
  }
  if (energy <= 0) return MIN_AOI_PAD_M;
  const combined = 10 * Math.log10(energy);
  const radius = 10 ** ((combined - 45) / 20);
  return Number.isFinite(radius) ? radius : MIN_AOI_PAD_M;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundCoord(value: number): number {
  const factor = 10 ** NOISE_RING_COORD_DECIMALS;
  return Math.round(value * factor) / factor;
}

/* ── Field builder ────────────────────────────────────────────────────── */

export interface BuildNoiseFieldArgs {
  noiseSources: readonly GenerationNoiseUnit[];
  geometry: GenerationNoiseGeometry;
  barriers?: readonly GenerationAcousticBarrier[];
  receptors?: readonly NoiseReceptor[];
  options?: NoiseFieldOptions;
}

export function buildNoiseField(args: BuildNoiseFieldArgs): NoiseFieldResult {
  const options: ResolvedOptions = { ...DEFAULT_OPTIONS, ...args.options };
  const barriers = args.barriers ?? [];
  const receptors = args.receptors ?? [];
  validateAcousticBarriers(barriers);

  // Origin: mean of every declared emitter position, iterated in sorted
  // entry order so permuted geometry arrays yield identical output.
  const allPositions = [...args.geometry.sources]
    .sort((a, b) => (a.noiseSourceId < b.noiseSourceId ? -1 : a.noiseSourceId > b.noiseSourceId ? 1 : 0))
    .flatMap((entry) => entry.positions);
  if (allPositions.length === 0) {
    throw new RangeError('noiseGeometry declares no emitter positions');
  }
  for (const position of allPositions) assertFiniteLatLng(position, 'noise source position');
  const origin = {
    lat: allPositions.reduce((sum, p) => sum + p.lat, 0) / allPositions.length,
    lng: allPositions.reduce((sum, p) => sum + p.lng, 0) / allPositions.length,
  };

  const { local: sources, record: sourceRecord } = resolveSources(args.noiseSources, args.geometry, origin);
  if (sources.length === 0) {
    throw new RangeError('no acoustic sources with usable data (positive count + datum) were supplied');
  }
  const segments = localBarrierSegments(barriers, origin);

  // AOI: bbox(sources ∪ barrier vertices ∪ located receptors) padded to the
  // free-field 45 dBA radius, capped to the max extent.
  const pad = Math.min(Math.max(freeField45RadiusM(sources), MIN_AOI_PAD_M), options.maxExtentM);
  const localReceptors = receptors
    .filter((r): r is NoiseReceptor & { location: { lat: number; lng: number } } => r.location != null)
    .map((r) => ({ id: r.id, ...localAxesForPoint(r.location, origin, 0) }));

  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const extend = (x: number, y: number): void => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  };
  for (const s of sources) extend(s.xM, s.yM);
  for (const seg of segments) { extend(seg.x1, seg.y1); extend(seg.x2, seg.y2); }
  for (const r of localReceptors) extend(r.xM, r.yM);
  minX -= pad; maxX += pad; minY -= pad; maxY += pad;

  // Cap the AOI square to ±maxExtentM around the source centroid (local 0,0).
  const clampExtent = options.maxExtentM;
  if (minX < -clampExtent) minX = -clampExtent;
  if (minY < -clampExtent) minY = -clampExtent;
  if (maxX > clampExtent) maxX = clampExtent;
  if (maxY > clampExtent) maxY = clampExtent;

  const gridN = Math.max(8, Math.floor(Math.sqrt(options.nodeBudget)));
  const spanM = Math.max(maxX - minX, maxY - minY);
  const spacingM = spanM / (gridN - 1);
  if (!Number.isFinite(spacingM) || spacingM <= 0) {
    throw new RangeError('noise field AOI collapsed to zero extent');
  }

  const values = new Float64Array(gridN * gridN);
  for (let r = 0; r < gridN; r++) {
    for (let c = 0; c < gridN; c++) {
      const level = noiseLevelAtPointDbA(
        { xM: minX + c * spacingM, yM: minY + r * spacingM },
        sources,
        segments,
        options,
      );
      values[r * gridN + c] = level == null ? Number.NaN : level;
    }
  }

  // Marching squares works on the grid's own local frame (origin at node
  // (0,0)); shift rings back into the AOI frame, then to lat/lng.
  const grid: ScalarGrid = { columns: gridN, rows: gridN, spacingM, values };
  const bands = NOISE_FIELD_LEVELS_DBA.flatMap((levelDbA) => {
    const rings = marchingSquaresRings(grid, levelDbA).map((ring) =>
      ring.map((point) => {
        const geo = latLngForLocalAxes(
          { xM: minX + point.xM, yM: minY + point.yM },
          origin,
          0,
        );
        return { lat: roundCoord(geo.lat), lng: roundCoord(geo.lng) };
      }),
    );
    return rings.length > 0 ? [{ levelDbA, rings }] : [];
  });

  // Receptors: located ones sample the exact field kernel (map and table
  // agree by construction); unlocated ones use the v2 distance screen.
  const receptorLevels: NoiseFieldResult['receptorLevels'] = receptors.map((receptor) => {
    const located = receptor.location != null;
    let predicted: number | null;
    if (located) {
      const point = localAxesForPoint(receptor.location!, origin, 0);
      predicted = noiseLevelAtPointDbA(point, sources, segments, options);
    } else {
      predicted = distanceScreenDbA(receptor, sources, options);
    }
    if (predicted != null) {
      const flat = receptor.barrierLossDb ?? 0;
      predicted = flat > 0 ? predicted - flat : predicted;
    }
    return {
      receptorId: receptor.id,
      predictedDbA: predicted == null ? null : round2(predicted),
      basis: located ? 'field-sample' as const : 'distance-screen' as const,
    };
  });

  return {
    modelVersion: NOISE_FIELD_MODEL_VERSION,
    origin: { lat: roundCoord(origin.lat), lng: roundCoord(origin.lng) },
    gridSpacingM: round2(spacingM),
    gridColumns: gridN,
    gridRows: gridN,
    bands,
    receptorLevels,
    sources: sourceRecord,
    assumptions: [
      `Representative frequency ${REPRESENTATIVE_FREQUENCY_HZ} Hz (λ ≈ ${round2(WAVELENGTH_M)} m) for barrier diffraction.`,
      `Atmospheric absorption ${ATM_ABSORPTION_DBA_PER_KM} dBA/km (ISO 9613-1 ballpark, 10 °C / 70 % RH).`,
      options.groundAttenuation
        ? 'ISO 9613-2 simplified ground effect over acoustically soft ground.'
        : 'Ground effect disabled.',
      `Default source height ${DEFAULT_SOURCE_HEIGHT_M} m; receiver height ${options.receiverHeightM} m.`,
    ],
    caveats: [...NOISE_FIELD_CAVEATS],
  };
}

/**
 * v2 distance screen for receptors without a map location: all emitters
 * co-located at the receptor's stated distance (the v1 posture) with the v2
 * atmospheric + ground terms applied. Geometric barriers cannot apply
 * without a location; the receptor's flat `barrierLossDb` is applied by the
 * caller (buildNoiseField) for both bases.
 */
function distanceScreenDbA(
  receptor: NoiseReceptor,
  sources: readonly LocalNoiseSource[],
  options: ResolvedOptions,
): number | null {
  const distanceM = receptor.distanceM;
  if (!Number.isFinite(distanceM) || distanceM <= 0) return null;
  let totalEnergy = 0;
  let anyContribution = false;
  for (const source of sources) {
    if (source.energyShare <= 0) continue;
    let baseLevel: number;
    if (source.datum.kind === 'reference-spl') {
      if (distanceM < source.datum.referenceDistanceM) return null;
      baseLevel = source.datum.levelDbA
        - 20 * Math.log10(distanceM / source.datum.referenceDistanceM)
        - source.sourceBarrierLossDb;
    } else {
      baseLevel = source.datum.levelDbA
        + (source.datum.directivityCorrectionDb ?? 0)
        - 10 * Math.log10(4 * Math.PI * distanceM * distanceM)
        - source.sourceBarrierLossDb;
    }
    let extraLossDb = 0;
    if (options.atmosphericAbsorption) extraLossDb += atmosphericAbsorptionDb(distanceM);
    if (options.groundAttenuation) {
      const meanHeightM = (source.heightM + options.receiverHeightM) / 2;
      extraLossDb += groundAttenuationDb(distanceM, meanHeightM);
    }
    totalEnergy += source.energyShare * 10 ** ((baseLevel - extraLossDb) / 10);
    anyContribution = true;
  }
  if (!anyContribution || totalEnergy <= 0) return null;
  return 10 * Math.log10(totalEnergy);
}

/* ── Deterministic auto-placement from the parcel test-fit ────────────── */

/**
 * Distribute noise sources across placed generation-block centroids (sorted
 * by rectangle id); aggregate reference-SPL sources and layouts without
 * placed blocks anchor at the layout anchor. Never reads solver internals —
 * only published component coordinates.
 */
export function deriveNoiseGeometryFromTestFit(
  noiseSources: readonly GenerationNoiseUnit[],
  testFit: Pick<GenerationParcelTestFitResult, 'components' | 'anchor'>,
): GenerationNoiseGeometry {
  const blockCentroids = testFit.components
    .filter((component) => component.rectangle.kind === 'generation-block')
    .slice()
    .sort((a, b) => (a.rectangle.id < b.rectangle.id ? -1 : a.rectangle.id > b.rectangle.id ? 1 : 0))
    .map((component) => ringCentroid(component.coordinates));

  const anchor = { lat: testFit.anchor.lat, lng: testFit.anchor.lng };
  const usesBlocks = blockCentroids.length > 0;

  return {
    basis: usesBlocks ? 'layout-blocks' : 'anchor',
    sources: noiseSources.map((unit) => {
      const datum = datumForUnit(unit);
      const isAggregate = datum?.kind === 'reference-spl' && datum.scope === 'aggregate';
      const positions = !usesBlocks || isAggregate ? [anchor] : blockCentroids;
      return {
        noiseSourceId: unit.id,
        positions: positions.map((p) => ({ lat: p.lat, lng: p.lng })),
      };
    }),
  };
}

/** Vertex-mean centroid of a (possibly closed) lat/lng ring. */
function ringCentroid(ring: ReadonlyArray<{ lat: number; lng: number }>): { lat: number; lng: number } {
  const closed = ring.length > 1
    && ring[0].lat === ring[ring.length - 1].lat
    && ring[0].lng === ring[ring.length - 1].lng;
  const n = closed ? ring.length - 1 : ring.length;
  let lat = 0; let lng = 0;
  for (let i = 0; i < n; i++) { lat += ring[i].lat; lng += ring[i].lng; }
  return { lat: lat / n, lng: lng / n };
}
